import React, { useMemo, useEffect, useState, useCallback } from 'react';
import {
  Avatar,
  Typography,
  Badge,
  Box,
  Stack,
  Tooltip,
  Chip,
} from '@mui/material';
import guestApi from '../../api/guestApi';
import { useConciergeStore } from '../../store/conciergeStore';

// --- role helpers (back-compat with legacy 'guest'|'hotel') ---
function roleOf(sender) {
  if (!sender) return undefined;
  if (typeof sender === 'string') return sender === 'guest' ? 'guest' : 'staff';
  if (typeof sender === 'object') return sender.role || undefined;
  return undefined;
}
function senderDisplayOf(sender) {
  const role = roleOf(sender);
  if (sender && typeof sender === 'object') {
    return sender.displayName || sender.name || (role === 'guest' ? 'Guest' : role === 'staff' ? 'Staff' : undefined);
  }
  return role === 'guest' ? 'Guest' : role === 'staff' ? 'Staff' : undefined;
}
function senderSubroleOf(sender) {
  if (sender && typeof sender === 'object' && sender.meta) {
    return sender.meta.roleName || sender.meta.roleKey || undefined;
  }
  return undefined;
}

// Heuristic to avoid showing raw UIDs as names
function isLikelyUid(s) {
  if (!s || typeof s !== 'string') return false;
  const t = s.trim();
  if (!t || t.includes('@') || /\s/.test(t)) return false;
  // Long, mostly base62-ish identifiers are likely UIDs
  if (t.length < 20) return false;
  return /^[A-Za-z0-9_-]+$/.test(t);
}

function getEmail(thread) {
  return (
    thread?.profile?.contact?.email ||
    thread?.email ||
    thread?.guestEmail ||
    thread?.userProfile?.email ||
    thread?.user?.email ||
    thread?.lastMessage?.sender?.email ||
    null
  );
}

function getDisplayName(thread) {
  const email = getEmail(thread);

  // 1) Prefer real names coming from profile/check-in/guest
  const fullName =
    thread?.guestName ||
    thread?.profile?.identity?.fullName ||
    thread?.profile?.identity?.name ||
    thread?.userProfile?.fullName ||
    '';
  if (fullName && fullName.trim()) return fullName.trim();

  // 2) Next, consider any provided displayName but avoid UID-looking values
  const dn = thread?.displayName || thread?.user?.displayName || '';
  if (dn && dn.trim() && !isLikelyUid(dn)) return dn.trim();

  // 3) Derive from email local-part when available (clean separators)
  if (email) {
    const left = String(email).split('@')[0].replace(/[._-]+/g, ' ').trim();
    if (left) return left;
    return String(email);
  }

  // 4) Last resort: readable hint from UID
  const uid = thread?.userUid || thread?.userId || thread?.guestId || '';
  if (uid) return `Guest ${String(uid).slice(0,4)}…${String(uid).slice(-3)}`;
  return 'Guest';
}

/**
 * ConciergeThreadItem
 * A single thread row used by ConciergeThreadList.
 * Props:
 *  - thread: {
 *      id, userUid, guestEmail, lastMessage, lastAt, unread: { staff }
 *    }
 *  - active: boolean
 *  - onSelect: (thread) => void
 */
export default function ConciergeThreadItem({ thread, active = false, onSelect }) {
  const unread = thread?.unread?.staff || 0;

  const baseDisplayName = getDisplayName(thread);
  const [hydratedName, setHydratedName] = useState(null);
  const displayName = hydratedName || baseDisplayName;
  const emailForLine = getEmail(thread);

  const threadKey = useMemo(() => {
    if (!thread?.propertyId || !thread?.userUid) return null;
    return `${String(thread.propertyId)}:${thread.userUid}`;
  }, [thread?.propertyId, thread?.userUid]);

  const guestPresence = useConciergeStore(useCallback(
    (s) => (threadKey ? s.onlineStatusByThread?.[threadKey]?.guest : undefined),
    [threadKey]
  ));

  const threadGuestPresence = useMemo(() => {
    if (!thread) return undefined;
    const p = thread.presence?.guest || {};
    return {
      // allow various possible field names from backend payloads
      isOnline: typeof p.online === 'boolean' ? p.online : p.isOnline,
      lastActive:
        p.lastActiveAt ||
        p.lastActive ||
        thread.guestLastActiveAt ||
        (thread.lastSeen && (thread.lastSeen.guest || thread.lastSeen.user)) ||
        undefined,
    };
  }, [thread]);

  const mergedPresence = useMemo(() => {
    // Prefer live guestPresence from store
    if (guestPresence && (guestPresence.isOnline || guestPresence.lastActive)) {
      return guestPresence;
    }
    // Otherwise fallback to thread-level guest presence fields
    if (threadGuestPresence && (threadGuestPresence.isOnline || threadGuestPresence.lastActive)) {
      return threadGuestPresence;
    }
    return undefined;
  }, [guestPresence, threadGuestPresence]);

  const presenceMeta = useMemo(() => {
    if (!mergedPresence) return null;
    const now = Date.now();
    const ONLINE_WINDOW_MS = 20_000;
    let lastMs;
    if (mergedPresence.lastActive) {
      const parsed = Date.parse(String(mergedPresence.lastActive));
      if (Number.isFinite(parsed)) lastMs = parsed;
    }
    const fresh = lastMs != null ? (now - lastMs) <= ONLINE_WINDOW_MS && (now - lastMs) >= 0 : false;
    const online = !!mergedPresence.isOnline && fresh;
    if (online) {
      return { label: 'Online', color: 'success', variant: 'filled' };
    }
    if (!lastMs) return null;
    const diffMs = now - lastMs;
    if (diffMs < 60_000) return { label: 'Active just now', color: 'default', variant: 'outlined' };
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 60) return { label: `Active ${minutes}m ago`, color: 'default', variant: 'outlined' };
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return { label: `Active ${hours}h ago`, color: 'default', variant: 'outlined' };
    const days = Math.floor(hours / 24);
    if (days < 7) return { label: `Active ${days}d ago`, color: 'default', variant: 'outlined' };
    try {
      const formatted = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(lastMs);
      return { label: `Active ${formatted}`, color: 'default', variant: 'outlined' };
    } catch {
      return { label: `Active ${days}d ago`, color: 'default', variant: 'outlined' };
    }
  }, [mergedPresence]);

  // Debug: inspect raw backend data feeding this row
  useEffect(() => {
    if (!thread) return;
  }, [thread, displayName]);

  // Hydrate friendly name from /api/user/:uid when we only have a UID-ish label
  useEffect(() => {
    const uid = thread?.userUid;
    if (!uid) return;
    const looksLikeFallback =
      isLikelyUid(baseDisplayName) ||
      /^Guest\s/.test(baseDisplayName) ||
      /^TEST_GUEST_|^EIGHTFOLD_GUEST_/i.test(baseDisplayName);
    if (!looksLikeFallback) return;

    let cancelled = false;
    (async () => {
      try {
        const user = await guestApi.getByUid(uid);
        const full = user?.profile?.identity?.fullName;
        const mail = user?.email;
        const next =
          (full && full.trim()) ||
          (mail && String(mail).split('@')[0].replace(/[._-]+/g, ' ').trim()) ||
          null;
        if (!cancelled && next && next !== hydratedName) {
          setHydratedName(next);
        }
      } catch (e) {
      }
    })();
    return () => { cancelled = true; };
  }, [thread?.userUid, baseDisplayName, hydratedName]);

  const initials = useMemo(() => {
    const name = (displayName || '').trim();
    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      const letters = ((parts[0] && parts[0][0]) || '') + ((parts[1] && parts[1][0]) || '');
      return (letters || 'G').toUpperCase();
    }
    const em = getEmail(thread);
    if (em && typeof em === 'string' && em.length) return em[0].toUpperCase();
    return 'G';
  }, [displayName, thread]);

  // Build a role-aware subtitle if thread carries last sender info
  const lastSender = thread?.lastSender || thread?.lastActor || thread?.lastBy || undefined;
  const who = senderDisplayOf(lastSender);
  const subrole = senderSubroleOf(lastSender);
  const prefix = who ? (subrole ? `${who} · ${subrole}` : who) : undefined;
  const previewText = thread?.lastMessage || '—';
  const subtitle = prefix ? `${prefix}: ${previewText}` : previewText;

  return (
    <Box
      onClick={() => onSelect && onSelect(thread)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        paddingY: 1,
        paddingX: 2,
        cursor: 'pointer',
        bgcolor: active ? 'action.selected' : 'background.paper',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        userSelect: 'none',
      }}
    >
       <Badge
    color="error"
    badgeContent={unread}
    invisible={!unread}
    overlap="circular"
    sx={{ mr: 2 }}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
  >
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      variant="dot"
      color="success"
      invisible={presenceMeta?.label !== 'Online'}
      sx={{
        '& .MuiBadge-badge': {
          width: 10,
          height: 10,
          borderRadius: '50%',
          boxShadow: (theme) => `0 0 0 2px ${theme.palette.common.white}`,
        },
      }}
    >
      <Avatar sx={{ width: 40, height: 40 }}>{initials}</Avatar>
    </Badge>
  </Badge>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Tooltip title={emailForLine ? String(emailForLine) : ''} placement="top-start" enterDelay={300}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                noWrap
                sx={{ cursor: emailForLine ? 'help' : 'default' }}
              >
                {displayName}
              </Typography>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {formatTime(thread?.lastAt)}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          sx={{ mt: 0.25 }}
          title={subtitle || ''}
        >
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
}

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString();
}
